import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineKeySkeletonAltIcon from 'LuxKit/Icons/line/LineKeySkeletonAltIcon'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import getLoyaltyNextTier from 'loyalty/selectors/getLoyaltyNextTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useLuxPlusRenewalBonus, { getCurrentRenewalRewardStep } from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import useJoinSociete, { joinSocieteMessages } from 'luxPlus/hooks/useJoinSociete'
import { RenewalTextSkeleton, renewalBonusMessages } from '../LandingPage/LuxPlusRenewalBonusTimeline'
import LuxPlusBenefitsCard, { type LuxPlusBenefitItem } from './LuxPlusBenefitsCard'
import { LUX_PLUS_MEMBER_BENEFITS, LUX_PLUS_MEMBER_BENEFIT_MESSAGES } from './LuxPlusManageAccountModalBenefitsList'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getLuxPlusSubscriptionRenewalOrExpiryDate } from 'selectors/luxPlusSelectors'
import { useCallback, useState, type ReactNode } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'

// Mobile uses the shorter "Your {program} benefits" heading; tablet+ uses the full sentence.
const MobileOnly = styled.span`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`
const TabletUpOnly = styled.span`
  display: none;
  ${mediaQueryUp.tablet} {
    display: inline;
  }
`

const messages = defineMessages({
  title: {
    id: 'VATamyUiEztR3iJWj2b9',
    defaultMessage: 'Success! Your {programName} membership is set to renew.',
    description: 'Title confirming the membership renewal has been re-enabled',
  },
  bodyBonusFull: {
    id: 'zX3DeLMXhUm9GOcATkap',
    defaultMessage: "You'll earn a renewal bonus of {loyaltyPoints} and {statusCredits} when your membership renews on <b>{date}</b>.",
    description: 'Tells the member the points and status credits they will earn on renewal',
  },
  bodyBonusPoints: {
    id: 'EAy_MXuzNBiQ-BNwtwvx',
    defaultMessage: "You'll earn a renewal bonus of {loyaltyPoints} when your membership renews on <b>{date}</b>.",
    description: 'Tells the member the points they will earn on renewal',
  },
  joinBonusFull: {
    id: 'VAp-WPrM6MslKCV6g3fn',
    defaultMessage: 'Join Société for free before your membership renews on <b>{date}</b> to receive a renewal bonus of {loyaltyPoints} and {statusCredits}.',
    description: 'Invites a non-Société member to join to earn the renewal bonus points and status credits',
  },
  joinBonusPoints: {
    id: 'g8cF6jJfZxVSn7iJVpXM',
    defaultMessage: 'Join Société for free before your membership renews on <b>{date}</b> to receive a renewal bonus of {loyaltyPoints}.',
    description: 'Invites a non-Société member to join to earn the renewal bonus points',
  },
  bodyNoBonus: {
    id: '5Tnvx5orOv-N4u21skaZ',
    defaultMessage: 'Your {programName} membership is set to renew on <b>{date}</b>, so you can keep enjoying your member benefits.',
    description: 'Confirms the renewal date when there is no renewal bonus to show',
  },
  benefitsHeading: {
    id: 'pmwpgsbaskblT0JZh1jl',
    defaultMessage: "As a {programName} member you'll continue to enjoy:",
    description: 'Heading above the member benefits list on the reactivation success screen (tablet and up)',
  },
  benefitsHeadingMobile: {
    defaultMessage: 'Your {programName} benefits',
    id: '2WqEmqMIPfWZOnor-_RY',
    description: 'Shorter heading above the member benefits list on the reactivation success screen on mobile',
  },
  plusMore: {
    defaultMessage: 'Plus so much more',
    id: 'mBLwqqePKPZRfzEK_wjd',
    description: 'Link that expands the member benefits list to show every LuxPlus+ benefit',
  },
  footnote: {
    id: 'nBAYQdCqCUQycYUNEhN8',
    defaultMessage: 'Renewal bonus offer only available on renewal of {programName} membership. <link>Terms and conditions apply.</link>',
    description: 'Disclaimer with terms and conditions link for the renewal bonus offer',
  },
  allDone: {
    id: 'V6qCDk20BAuJ2OYvm5W7',
    defaultMessage: 'All done',
    description: 'Primary button text to dismiss the renewal reactivation success modal',
  },
  moreOnLoyalty: {
    id: 't6DmD_LF9qTdVfh7K8EW',
    defaultMessage: 'More on {loyaltyProgramName}',
    description: 'Secondary button text linking to loyalty program page from renewal success modal',
  },
  memberPricingTitle: { defaultMessage: 'Member pricing on hotels, flights, experiences and more', id: '5BsZrXJHsq-88Tw80pKJ', description: 'LuxPlus+ member benefit headline for member pricing on the reactivation success benefits summary' },
  memberPricingBody: { defaultMessage: 'Save hundreds every year on our best offers', id: '4jnXrMzRtgIYkM6dYB6K', description: 'LuxPlus+ member benefit subtext for member pricing on the reactivation success benefits summary' },
})

/**
 * Re-enable renewal success screen. Only reached for loyalty-enabled brands — non-loyalty
 * brands return to the account overview after re-enabling, with no success screen.
 */
function LuxPlusManageAccountModalRenewalReactivationSuccessTiered() {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const isInfinite = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  // Top tiers have no next tier to progress to: platinum yields no next tier, and infinite sits
  // outside the public tier ladder entirely.
  const nextTier = useAppSelector(getLoyaltyNextTier)
  const showNextTierBlurb = !!nextTier && !isInfinite
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalOrExpiryDate)
  const { steps, isLoading } = useLuxPlusRenewalBonus()

  const joinSociete = useJoinSociete()

  const nextReward = getCurrentRenewalRewardStep(steps)
  const points = nextReward?.points
  const statusCredits = nextReward?.statusCredits

  // The renewal bonus is shown to both members (what they'll earn) and non-members (what they'd earn
  // by joining Société for free). Only the framing and the secondary CTA differ.
  const hasBonusAmounts = points !== undefined
  const showStatusCredits = hasBonusAmounts && !hideStatusCredits && statusCredits !== undefined

  const date = renewalDate ? <FormatDateTime value={renewalDate} dateStyle="medium" /> : null
  const b = (chunks: Array<ReactNode>) => <b>{chunks}</b>

  const bonusBody = isSocieteMember
    ? (showStatusCredits ? messages.bodyBonusFull : messages.bodyBonusPoints)
    : (showStatusCredits ? messages.joinBonusFull : messages.joinBonusPoints)

  const [benefitsExpanded, setBenefitsExpanded] = useState(false)
  const summaryBenefits: Array<LuxPlusBenefitItem> = [
    { icon: LineBedIcon, title: messages.memberPricingTitle, body: messages.memberPricingBody },
    { icon: LineStarIcon, title: LUX_PLUS_MEMBER_BENEFIT_MESSAGES.inclusionsTitle, body: LUX_PLUS_MEMBER_BENEFIT_MESSAGES.inclusionsBody },
    { icon: LineKeySkeletonAltIcon, title: LUX_PLUS_MEMBER_BENEFIT_MESSAGES.hiddenTitle },
  ]

  return <>
    <ModalHeader title={intl.formatMessage(messages.title, { programName: LUXURY_PLUS.PROGRAM_NAME })} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24}>
          {isLoading
            ? <RenewalTextSkeleton lineWidths={[320, 300, 160]} height={18} />
            : <BodyText variant="medium" colour="light-foreground-alt">
                {hasBonusAmounts && <>
                  <FormattedMessage
                    {...bonusBody}
                    values={{
                      date,
                      b,
                      loyaltyPoints: <FormatLoyaltyPoints points={points} />,
                      statusCredits: <FormatLoyaltyStatusCredits statusCredits={statusCredits ?? 0} />,
                    }}
                  />
                  {showNextTierBlurb && <>{' '}<FormattedMessage
                    {...renewalBonusMessages.nextTierBlurb}
                    values={{ nextTier: getLoyaltyTierDisplayName(nextTier) }}
                  />
                  </>}
                </>}
                {!hasBonusAmounts && <FormattedMessage
                  {...messages.bodyNoBonus}
                  values={{ programName: LUXURY_PLUS.PROGRAM_NAME, date, b }}
                />}
            </BodyText>}
          <Group direction="vertical" gap={12}>
            <BodyText variant="medium" weight="bold">
              <MobileOnly>{intl.formatMessage(messages.benefitsHeadingMobile, { programName: LUXURY_PLUS.PROGRAM_NAME })}</MobileOnly>
              <TabletUpOnly>{intl.formatMessage(messages.benefitsHeading, { programName: LUXURY_PLUS.PROGRAM_NAME })}</TabletUpOnly>
            </BodyText>
            <LuxPlusBenefitsCard
              items={benefitsExpanded ? LUX_PLUS_MEMBER_BENEFITS : summaryBenefits}
              footer={!benefitsExpanded && <TextLink
                size="medium"
                weight="regular"
                onClick={() => setBenefitsExpanded(true)}
              >
                {intl.formatMessage(messages.plusMore)}
              </TextLink>}
            />
          </Group>
          <BodyText variant="small" colour="light-foreground-alt">
            <FormattedMessage
              {...messages.footnote}
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                link: (chunks) => <TextLink to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} weight="regular" onClick={dismissModal}>{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </Group>
      </ModalContent>
    </ModalBody>
    <ProductPaletteProvider palette="lux-plus">
      <ModalFooter
        // Stack on mobile ("All done" above the secondary link); side-by-side on tablet+.
        actionsLayout="stacked"
        tabletActionsLayout="auto"
        primaryActionProps={{ children: intl.formatMessage(messages.allDone), onClick: dismissModal }}
        secondaryActionProps={isSocieteMember
          ? {
              children: intl.formatMessage(messages.moreOnLoyalty, { loyaltyProgramName: getLoyaltyProgramDisplayName() }),
              to: `/${LOYALTY_LANDING_PAGE}`,
            }
          : {
              children: intl.formatMessage(joinSocieteMessages.cta),
              onClick: joinSociete,
            }}
      />
    </ProductPaletteProvider>
  </>
}

export default LuxPlusManageAccountModalRenewalReactivationSuccessTiered
